import React from "react"
import type { DeepPartial } from "ai"

import { SpeakerIcon } from "@/components/SpeakerIcon"
import { CardStack } from "@/components/cardStack/CardStack"
import { TERM_CARD_BACKGROUND_CLASSES } from "@/components/vocabulary/term-card-backgrounds"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer"
import { cn } from "@/lib/utils"
import type { StackableItem } from "@/lib/types"
import type { SwipeDirection } from "@/hooks/useCardRotation"
import {
  ArrowLeftRight,
  ArrowUpRight,
  ChevronDown,
  Eye,
  EyeOff,
  ClipboardCopy,
  Check,
  Loader2,
  Volume2,
} from "lucide-react"
import { useI18n } from "@/lib/i18n"
import { createVocabularyTermKey } from "@/lib/vocabulary-term-key"
import { useWordExplanation } from "@/hooks/use-word-explanation"
import { toast } from "sonner"

import "@/components/vocabulary-display.css"

export type VocabularyExample = {
  sentence?: string
  translation?: string | Record<string, string>
  english?: string
  timestamp?: number | string
  endTimestamp?: number | string
  type?: string
  context_note?: string | Record<string, string>
}

export type VocabularyTerm = {
  term: string
  explanation: string | Record<string, string>
  timestamp?: number | string
  endTimestamp?: number | string
  examples: VocabularyExample[]
  context_description?: string
}

export type VocabularyScenario = {
  name: string
  terms: VocabularyTerm[]
}

export type VocabularyAnalysis = {
  scenarios: VocabularyScenario[]
}

// 🎯 实时更新单条解释翻译
const updateSingleExplanation = (
  vocabulary: DeepPartial<VocabularyAnalysis>,
  scenarioIndex: number,
  termIndex: number,
  translation: string,
  languageCode: string
): DeepPartial<VocabularyAnalysis> => {
  if (!vocabulary?.scenarios) return vocabulary;
  
  const updated = JSON.parse(JSON.stringify(vocabulary)) as DeepPartial<VocabularyAnalysis>;
  const scenario = updated.scenarios?.[scenarioIndex];
  const term = scenario?.terms?.[termIndex];
  
  if (term) {
    const existing = term.explanation;
    const newObj: Record<string, string> = {};
    
    if (typeof existing === 'string') {
      newObj['zh'] = existing;
    } else if (existing && typeof existing === 'object') {
      Object.entries(existing).forEach(([k, v]) => {
        if (v !== undefined) newObj[k] = v;
      });
    }
    newObj[languageCode] = translation;
    (term as any).explanation = newObj;
  }
  
  return updated;
};

// 🎯 实时更新单条例句翻译
const updateSingleExampleTranslation = (
  vocabulary: DeepPartial<VocabularyAnalysis>,
  scenarioIndex: number,
  termIndex: number,
  exampleIndex: number,
  translation: string,
  languageCode: string
): DeepPartial<VocabularyAnalysis> => {
  if (!vocabulary?.scenarios) return vocabulary;
  
  const updated = JSON.parse(JSON.stringify(vocabulary)) as DeepPartial<VocabularyAnalysis>;
  const scenario = updated.scenarios?.[scenarioIndex];
  const term = scenario?.terms?.[termIndex];
  const example = term?.examples?.[exampleIndex];
  
  if (example) {
    const existing = example.translation;
    const newObj: Record<string, string> = {};
    
    if (typeof existing === 'string') {
      newObj['zh'] = existing;
    } else if (existing && typeof existing === 'object') {
      Object.entries(existing).forEach(([k, v]) => {
        if (v !== undefined) newObj[k] = v;
      });
    }
    newObj[languageCode] = translation;
    (example as any).translation = newObj;
  }
  
  return updated;
};

// 🎯 实时更新单条 context_note 翻译
const updateSingleContextNote = (
  vocabulary: DeepPartial<VocabularyAnalysis>,
  scenarioIndex: number,
  termIndex: number,
  exampleIndex: number,
  translation: string,
  languageCode: string
): DeepPartial<VocabularyAnalysis> => {
  if (!vocabulary?.scenarios) return vocabulary;
  
  const updated = JSON.parse(JSON.stringify(vocabulary)) as DeepPartial<VocabularyAnalysis>;
  const scenario = updated.scenarios?.[scenarioIndex];
  const term = scenario?.terms?.[termIndex];
  const example = term?.examples?.[exampleIndex];
  
  if (example) {
    const existing = example.context_note;
    const newObj: Record<string, string> = {};
    
    if (typeof existing === 'string') {
      newObj['zh'] = existing;
    } else if (existing && typeof existing === 'object') {
      Object.entries(existing).forEach(([k, v]) => {
        if (v !== undefined) newObj[k] = v;
      });
    }
    newObj[languageCode] = translation;
    (example as any).context_note = newObj;
  }
  
  return updated;
};

const formatTimestamp = (timestamp: number | string | undefined | null) => {
  if (timestamp === undefined || timestamp === null) return "0:00"

  if (typeof timestamp === "string") {
    if (/^\d{2}:\d{2}:\d{2}$/.test(timestamp)) {
      const [hours, minutes, seconds] = timestamp.split(":" ).map(Number)
      const totalMinutes = hours * 60 + minutes
      return `${totalMinutes}:${seconds.toString().padStart(2, "0")}`
    }

    if (/^\d{1,2}:\d{2}$/.test(timestamp)) {
      return timestamp
    }

    const numSeconds = parseFloat(timestamp)
    if (Number.isNaN(numSeconds)) {
      console.warn("Invalid timestamp:", timestamp)
      return "0:00"
    }
    timestamp = numSeconds
  }

  const totalSeconds = Math.round(timestamp)
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
}

const parseTimestampToSeconds = (
  timestamp: number | string | undefined | null
): number | null => {
  if (timestamp === undefined || timestamp === null) {
    return null
  }

  if (typeof timestamp === "number" && !Number.isNaN(timestamp)) {
    return timestamp
  }

  if (typeof timestamp === "string") {
    if (/^\d{2}:\d{2}:\d{2}$/.test(timestamp)) {
      const [hours, minutes, seconds] = timestamp.split(":" ).map(Number)
      return hours * 3600 + minutes * 60 + seconds
    }

    if (/^\d{1,2}:\d{2}$/.test(timestamp)) {
      const [minutes, seconds] = timestamp.split(":" ).map(Number)
      return minutes * 60 + seconds
    }

    const numSeconds = parseFloat(timestamp)
    if (!Number.isNaN(numSeconds)) {
      return numSeconds
    }
  }

  return null
}

type TermStackItem = StackableItem & {
  term: Partial<VocabularyTerm>
  scenarioName?: string
  backgroundClass: string
}

type ProcessedScenario = {
  scenario: DeepPartial<VocabularyScenario> | undefined
  scenarioValue: string
  scenarioLabel: string
  termStackItems: TermStackItem[]
}

const STACK_BATCH_SIZE = 24

interface ScenarioStackState {
  visible: TermStackItem[]
  queue: TermStackItem[]
  dismissed: TermStackItem[]
  signature: string
}

const arraysEqualById = (a: TermStackItem[], b: TermStackItem[]) => {
  if (a.length !== b.length) {
    return false
  }

  for (let index = 0; index < a.length; index += 1) {
    if (String(a[index].id) !== String(b[index].id)) {
      return false
    }
  }

  return true
}

const dedupeById = <T extends { id: string | number }>(list: T[]): T[] => {
  const seen = new Set<string>()
  const result: T[] = []

  list.forEach((item) => {
    const key = String(item.id)
    if (!seen.has(key)) {
      seen.add(key)
      result.push(item)
    }
  })

  return result
}

const MetaChip = ({ label, value }: { label: string; value: string }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-muted/40 px-2.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
    <span className="text-muted-foreground/70">{label}</span>
    <span className="font-semibold text-foreground">{value}</span>
  </span>
)

interface ExampleCardProps {
  example: Partial<VocabularyExample>
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void
  userLanguage?: string
  isGeneratingTranslation?: boolean
}

const ExampleCard: React.FC<ExampleCardProps> = ({ example, onTimestampClick, userLanguage, isGeneratingTranslation = false }) => {
  const speakRef = React.useRef<HTMLDivElement>(null)
  const translationId = React.useId()
  const contentId = React.useId()
  const [showTranslation, setShowTranslation] = React.useState(false)
  const isPractical = example?.type === "practical"
  const [isCollapsed, setIsCollapsed] = React.useState(() => isPractical)

  const exampleText = example?.english || example?.sentence || ""
  
  // 🔥 移除 useMemo，直接计算以确保每次渲染都获取最新值
  const raw = example?.translation
  const translation = (() => {
    // If user's language is not Chinese, only show translation for user's language, never show Chinese fallback
    if (userLanguage && userLanguage !== "zh") {
      // If it's a string (legacy format), assume it's Chinese - don't show it
      if (typeof raw === "string") {
        return ""
      }
      
      // If it's an object, only return user's language translation
      if (typeof raw === "object" && raw !== null) {
        const userLangTranslation = raw[userLanguage]
        // Only return if we have a valid non-empty translation for user's language
        if (userLangTranslation && typeof userLangTranslation === "string" && String(userLangTranslation).trim().length > 0) {
          return String(userLangTranslation)
        }
        // Don't show Chinese or any other language fallback
        return ""
      }
      
      return ""
    }
    
    // If user language is Chinese, show Chinese or any available translation
    if (typeof raw === "string") return raw
    if (typeof raw === "object" && raw !== null) {
      const lang = userLanguage || "zh"
      return raw[lang] || raw["zh"] || Object.values(raw)[0] || ""
    }
    return ""
  })()

  // Check if example has translation for user's language
  const hasTranslation = React.useMemo(() => {
    const raw = example?.translation;
    if (!raw) return false;
    
    if (typeof raw === "string") {
      // Legacy string format - assume Chinese, so if userLanguage is not Chinese, it doesn't have translation
      return userLanguage === "zh";
    }
    
    if (typeof raw === "object" && raw !== null) {
      const lang = userLanguage || "zh";
      const trans = raw[lang];
      return Boolean(trans && typeof trans === "string" && String(trans).trim().length > 0);
    }
    
    return false;
  }, [example?.translation, userLanguage]);

  // Check if context_note has translation for user's language
  const hasContextNoteTranslation = React.useMemo(() => {
    const raw = example?.context_note;
    if (!raw) return false;
    
    if (typeof raw === "string") {
      // Legacy string format - assume Chinese, so if userLanguage is not Chinese, it doesn't have translation
      return userLanguage === "zh";
    }
    
    if (typeof raw === "object" && raw !== null) {
      const lang = userLanguage || "zh";
      const translation = raw[lang];
      return Boolean(translation && typeof translation === "string" && String(translation).trim().length > 0);
    }
    
    return false;
  }, [example?.context_note, userLanguage]);

  // Get context_note translation for user's language
  const contextNote = React.useMemo(() => {
    const raw = example?.context_note;
    if (!raw) return "";
    
    // If user's language is not Chinese, only show translation for user's language, never show Chinese fallback
    if (userLanguage && userLanguage !== "zh") {
      // If it's a string (legacy format), assume it's Chinese - don't show it
      if (typeof raw === "string") {
        return "";
      }
      
      // If it's an object, only return user's language translation
      if (typeof raw === "object" && raw !== null) {
        const userLangTranslation = raw[userLanguage];
        // Only return if we have a valid non-empty translation for user's language
        if (userLangTranslation && typeof userLangTranslation === "string" && String(userLangTranslation).trim().length > 0) {
          return String(userLangTranslation);
        }
        // Don't show Chinese or any other language fallback
        return "";
      }
      
      return "";
    }
    
    // If user language is Chinese, show Chinese or any available translation
    if (typeof raw === "string") {
      return raw;
    }
    
    if (typeof raw === "object" && raw !== null) {
      const lang = userLanguage || "zh";
      return raw[lang] || raw["zh"] || Object.values(raw)[0] || "";
    }
    
    return "";
  }, [example?.context_note, userLanguage]);

  const exampleTimestamp = parseTimestampToSeconds(example?.timestamp)
  const exampleEndTimestamp = parseTimestampToSeconds(example?.endTimestamp)
  const typeLabel = example?.type
    ? example.type === "practical"
      ? "Practical"
      : example.type === "original"
        ? "Original"
        : example.type
    : undefined

  React.useEffect(() => {
    if (isCollapsed) {
      setShowTranslation(false)
    }
  }, [isCollapsed])

  if (!exampleText) return null

  return (
    <div className="group/example rounded-3xl border border-dashed border-border/50 bg-muted/40 p-3">
      <div className="relative space-y-1.5">
        {isPractical && (
          <button
            type="button"
            className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground/70 transition hover:text-primary"
            onClick={(event) => {
              event.stopPropagation()
              setIsCollapsed((prev) => !prev)
            }}
            aria-expanded={!isCollapsed}
            aria-controls={contentId}
          >
            <ChevronDown
              className={cn(
                "h-3 w-3 transition-transform",
                !isCollapsed && "rotate-180"
              )}
            />
            <span>{isCollapsed ? "Show practical example" : "Hide practical example"}</span>
          </button>
        )}

        {!isCollapsed && (
          <div id={contentId} className="space-y-1.5">
            <p className="text-sm font-medium leading-relaxed text-foreground/90">"{exampleText}"</p>
            {/* 🎯 翻译区域：有翻译显示翻译，正在生成且无翻译显示 skeleton */}
            {translation ? (
              <div className="space-y-1">
                <button
                  type="button"
                  className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground/70 transition hover:text-primary"
                  onClick={(event) => {
                    event.stopPropagation()
                    setShowTranslation((prev) => !prev)
                  }}
                  aria-expanded={showTranslation}
                  aria-controls={translationId}
                >
                  <ChevronDown
                    className={cn(
                      "h-3 w-3 transition-transform",
                      showTranslation && "rotate-180"
                    )}
                  />
                  <span>{showTranslation ? "Hide translation" : "Show translation"}</span>
                </button>
                {showTranslation && (
                  <p
                    id={translationId}
                    className="rounded-2xl bg-muted/40 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground/80"
                  >
                    {translation || <span className="text-muted-foreground/50 italic">翻译加载中...</span>}
                  </p>
                )}
              </div>
            ) : userLanguage && userLanguage !== "zh" && !hasTranslation && isGeneratingTranslation ? (
              <div className="space-y-1 mt-1">
                <Skeleton className="h-3 w-full rounded-lg" />
                <Skeleton className="h-3 w-3/4 rounded-lg" />
              </div>
            ) : null}
            {userLanguage && userLanguage !== "zh" && !hasContextNoteTranslation && !contextNote && isGeneratingTranslation ? (
              <div className="space-y-1">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            ) : contextNote ? (
              <p className="text-[13px] font-[400] italic text-muted-foreground/70">{contextNote}</p>
            ) : null}
          </div>
        )}

        {!isCollapsed && (
          <div className="absolute -right-2 -top-5.5 flex items-center gap-1 opacity-100 pointer-events-auto transition-opacity duration-150 sm:pointer-events-none sm:opacity-0 sm:group-hover/example:pointer-events-auto sm:group-hover/example:opacity-100">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full border text-muted-foreground/70 hover:text-primary"
              onClick={(event) => {
                event.stopPropagation()
                speakRef.current?.click()
              }}
            >
              <Volume2 className="h-4 w-4" />
            </Button>
            {exampleTimestamp !== null && onTimestampClick && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full border text-muted-foreground/80 hover:text-primary"
                onClick={(event) => {
                  event.stopPropagation()
                  onTimestampClick(exampleTimestamp, exampleEndTimestamp ?? undefined)
                }}
                title={`Jump to ${formatTimestamp(exampleTimestamp)}`}
              >
                <ArrowUpRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {typeLabel && (
          <Badge
            variant="outline"
            className={cn(
              "rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-wide",
              isPractical ? "bg-primary text-background border-primary/40" : "bg-foreground border-border/70 text-background"
            )}
          >
            {typeLabel}
          </Badge>
        )}
        {exampleTimestamp !== null && !onTimestampClick && (
          <MetaChip label="Timestamp" value={formatTimestamp(exampleTimestamp)} />
        )}
      </div>

      <div className="sr-only">
        <SpeakerIcon divRef={speakRef} lang="en" text={exampleText} />
      </div>
    </div>
  )
}

interface TermCardProps {
  term?: Partial<VocabularyTerm> | null
  scenarioName?: string
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void
  forceReveal?: boolean
  backgroundClass?: string
  onSave?: () => Promise<void> | void
  isSaving?: boolean
  isSaved?: boolean
  saveLabel?: string
  savedLabel?: string
  savingLabel?: string
  disabledReason?: string
  onMarkKnown?: () => void
  onKeepLearning?: () => void
  variant?: "stack" | "grid"
  userLanguage?: string
  isGeneratingTranslation?: boolean
}

const TermCard: React.FC<TermCardProps> = (props) => {
  const {
    term,
    scenarioName,
    onTimestampClick,
    forceReveal,
    backgroundClass,
    onSave,
    isSaving = false,
    isSaved = false,
    saveLabel = "Save",
    savedLabel = "Saved",
    savingLabel = "Saving...",
    disabledReason,
    onMarkKnown,
    onKeepLearning,
    variant = "grid",
    userLanguage,
    isGeneratingTranslation = false,
  } = props

  const speakRef = React.useRef<HTMLDivElement>(null)
  const [isExplanationHidden, setIsExplanationHidden] = React.useState(true)
  const previousHiddenRef = React.useRef(true)
  const { t } = useI18n()
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false)
  const isStackVariant = variant === "stack"
  const [showStackExampleTranslation, setShowStackExampleTranslation] =
    React.useState(false)

  const handleExplanationToggle = React.useCallback(
    (event?: React.SyntheticEvent) => {
      event?.stopPropagation()
      if (forceReveal) return
      const nextState = !isExplanationHidden
      previousHiddenRef.current = nextState
      setIsExplanationHidden(nextState)
    },
    [forceReveal, isExplanationHidden]
  )

  const handleExplanationKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLParagraphElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        handleExplanationToggle(event)
      }
    },
    [handleExplanationToggle]
  )

  React.useEffect(() => {
    if (forceReveal) {
      previousHiddenRef.current = isExplanationHidden
      setIsExplanationHidden(false)
    } else {
      setIsExplanationHidden(previousHiddenRef.current ?? true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceReveal])

  React.useEffect(() => {
    if (variant === "stack") {
      setIsDrawerOpen(false)
    }
  }, [variant, term?.term])

  React.useEffect(() => {
    setShowStackExampleTranslation(false)
  }, [term?.term, isStackVariant])

  const handleSaveClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()

      const result = onSave?.()
      if (result && typeof (result as Promise<void>).then === "function") {
        ;(result as Promise<void>).catch((error) => {
          console.error("Failed to save vocabulary term:", error)
        })
      }
    },
    [onSave]
  )

  // Check if explanation has user's language translation
  const hasUserLanguageTranslation = React.useMemo(() => {
    const raw = term?.explanation
    if (!raw) return false
    
    if (typeof raw === "string") {
      // Legacy string format - assume Chinese, so if userLanguage is not Chinese, it doesn't have translation
      return userLanguage === "zh"
    }
    
    if (typeof raw === "object" && raw !== null) {
      const lang = userLanguage || "zh"
      // Check if translation exists and is not empty
      const translation = raw[lang]
      return Boolean(translation && typeof translation === "string" && translation.trim().length > 0)
    }
    
    return false
  }, [term?.explanation, userLanguage])

  const explanation = React.useMemo(() => {
    const raw = term?.explanation
    if (!raw) return ""
    
    // If user's language is not Chinese, only show translation for user's language, never show Chinese fallback
    if (userLanguage && userLanguage !== "zh") {
      // If it's a string (legacy format), assume it's Chinese - don't show it
      if (typeof raw === "string") {
        return ""
      }
      
      // If it's an object, only return user's language translation
      if (typeof raw === "object" && raw !== null) {
        const userLangTranslation = raw[userLanguage]
        // Only return if we have a valid non-empty translation for user's language
        if (userLangTranslation && typeof userLangTranslation === "string" && userLangTranslation.trim().length > 0) {
          return userLangTranslation
        }
        // Don't show Chinese or any other language fallback
        return ""
      }
      
      return ""
    }
    
    // If user language is Chinese, show Chinese or any available translation
    if (typeof raw === "string") {
      return raw
    }
    
    if (typeof raw === "object" && raw !== null) {
      const lang = userLanguage || "zh"
      return raw[lang] || raw["zh"] || Object.values(raw)[0] || ""
    }
    
    return ""
  }, [term?.explanation, userLanguage, isGeneratingTranslation, hasUserLanguageTranslation])

  const contextDescription = term?.context_description
  const examples = Array.isArray(term?.examples) ? term.examples : []
  const displayedExamples = examples.slice(0, 2)
  const remainingExamples = Math.max(examples.length - displayedExamples.length, 0)
  const stackExamplePreview = React.useMemo(() => {
    if (!isStackVariant) return null
    const validExamples = examples.filter((example) => {
      const text = example?.english || example?.sentence
      return typeof text === "string" && text.trim().length > 0
    })
    if (validExamples.length === 0) return null
    const prioritized =
      validExamples.find((example) => example?.type !== "practical") ??
      validExamples[0]
    const text = prioritized?.english || prioritized?.sentence
    if (!text || text.trim().length === 0) return null
    const rawTranslation = prioritized?.translation
    const translation =
      typeof rawTranslation === "string"
        ? rawTranslation.trim()
        : typeof rawTranslation === "object" && rawTranslation !== null
        ? (rawTranslation[userLanguage || 'zh'] || rawTranslation['zh'] || Object.values(rawTranslation)[0] || "").trim()
        : ""
    return {
      text,
      translation: translation.length > 0 ? translation : undefined,
      isPractical: prioritized?.type === "practical",
      timestamp: parseTimestampToSeconds(prioritized?.timestamp),
      endTimestamp: parseTimestampToSeconds(prioritized?.endTimestamp) ?? undefined,
    }
  }, [examples, isStackVariant])
  const stackExampleRequiresDetails = Boolean(stackExamplePreview?.isPractical)
  const stackExampleTimestamp = stackExamplePreview?.timestamp ?? null
  const stackExampleEndTimestamp = stackExamplePreview?.endTimestamp
  const stackExampleText = stackExamplePreview?.text ?? ""
  const stackExampleTranslation = stackExamplePreview?.translation
  if (!term) {
    return null
  }
  const timestampSeconds = parseTimestampToSeconds(term.timestamp)
  const endTimestampSeconds = parseTimestampToSeconds(term.endTimestamp)
  const isBlurred = !forceReveal && isExplanationHidden
  const baseTermKey = term.term || "term"
  const hasDetails = Boolean(
    (explanation && explanation.trim().length > 0) ||
      (contextDescription && contextDescription.trim().length > 0) ||
      examples.length > 0
  )
  const drawerDescriptionLine = [
    scenarioName && scenarioName.trim() ? scenarioName : null,
    timestampSeconds !== null ? formatTimestamp(timestampSeconds) : null,
  ]
    .filter(Boolean)
    .join(" • ")

  const saveButtonDisabled = isSaved || isSaving || !onSave
  const saveButtonIcon = isSaving ? (
    <Loader2 className="h-4 w-4 animate-spin" />
  ) : isSaved ? (
    <Check className="h-4 w-4" />
  ) : (
    <ClipboardCopy className="h-4 w-4" />
  )
  const saveButtonTitle =
    isSaved || !disabledReason || !saveButtonDisabled ? (isSaved ? savedLabel : undefined) : disabledReason
  const baseAccessibleLabel = isSaving ? savingLabel : isSaved ? savedLabel : saveLabel
  const saveButtonAccessibleLabel =
    saveButtonTitle && saveButtonTitle.trim().length > 0
      ? saveButtonTitle
      : baseAccessibleLabel

  const title = term.term ? term.term.charAt(0).toUpperCase() + term.term.slice(1) : ""
  return (
    <div
      className={cn(
        "group/term relative flex h-full flex-col gap-4 rounded-[35px] border border-border p-4 shadow-sm ring-1 ring-transparent transition hover:shadow-lg hover:ring-primary/20",
        "bg-background/95"
      )}
    >
      <div className="flex-1 space-y-2 overflow-visible sm:overflow-y-auto sm:pr-1">
        <div className="space-y-1.5 pr-12">
          {title && <h3 className="text-lg sm:text-2xl font-sans font-semibold leading-tight text-foreground">{title}</h3>}
          {userLanguage && userLanguage !== "zh" && !hasUserLanguageTranslation && !explanation ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : explanation ? (
            <p
              className={cn(
                "text-sm leading-relaxed text-muted-foreground transition filter",
                isBlurred && "blur-sm select-none cursor-pointer",
                !isBlurred && isStackVariant && "max-h-24 overflow-hidden"
              )}
              {...(!forceReveal
                ? {
                    role: "button",
                    tabIndex: 0,
                    onClick: handleExplanationToggle,
                    onKeyDown: handleExplanationKeyDown,
                  }
                : {})}
            >
              {explanation}
            </p>
          ) : null}
        </div>

        <div className="absolute right-3 top-3 flex items-center gap-1 opacity-100 pointer-events-auto transition-opacity duration-150 sm:pointer-events-none sm:opacity-0 sm:group-hover/term:pointer-events-auto sm:group-hover/term:opacity-100">
          {title && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground/70 hover:text-primary"
              onClick={(event) => {
                event.stopPropagation()
                speakRef.current?.click()
              }}
            >
              <Volume2 className="h-4 w-4" />
            </Button>
          )}
          {explanation && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "h-7 w-7 text-muted-foreground/70 hover:text-primary",
                isBlurred && "text-primary"
              )}
              onClick={handleExplanationToggle}
              disabled={forceReveal}
              aria-pressed={!isBlurred}
              title={
                forceReveal
                  ? "Global reveal enabled"
                  : isExplanationHidden
                    ? "Show explanation"
                    : "Hide explanation"
              }
            >
              {isBlurred ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          )}
          {timestampSeconds !== null && onTimestampClick && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full bg-foreground/10 border text-muted-foreground/80 hover:text-primary"
              onClick={(event) => {
                event.stopPropagation()
                onTimestampClick(timestampSeconds, endTimestampSeconds ?? undefined)
              }}
              title={`Jump to ${formatTimestamp(timestampSeconds)}`}
            >
              <ArrowUpRight className="h-4 w-4" />
            </Button>
          )}
        </div>

        {(scenarioName || timestampSeconds !== null || examples.length > 0) && (
          <div className="flex flex-wrap items-center gap-2">
            {/* {scenarioName && (
              <Badge
                variant="outline"
                className="rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-wide"
              >
                {scenarioName}
              </Badge>
            )} */}
            {timestampSeconds !== null && (
              <MetaChip label="Timestamp" value={formatTimestamp(timestampSeconds)} />
            )}
            {examples.length > 0 && (
              <MetaChip
                label={t("vocabulary.card.examplesLabel", "Examples")}
                value={`${examples.length}`}
              />
            )}
          </div>
        )}

        {/* {contextDescription && (
          <div className="">
            <p className="text-xs leading-relaxed text-muted-foreground/80">{contextDescription}</p>
          </div>
        )} */}

        {isStackVariant && stackExamplePreview && (
          stackExampleRequiresDetails ? (
            <div className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 px-3 py-2 text-[11px] text-primary/80">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold uppercase tracking-wide">
                  {t("vocabulary.card.practicalExample", "Practical example")}
                </span>
                {hasDetails && (
                  <button
                    type="button"
                    className="rounded-full border border-primary/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-primary transition hover:bg-primary/10"
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      setIsDrawerOpen(true)
                    }}
                  >
                    {t("vocabulary.stack.viewDetails", "View details")}
                  </button>
                )}
              </div>
              <p className="mt-2 leading-relaxed">
                {t(
                  "vocabulary.card.practicalExampleHint",
                  "Open details to review the practical scenario and translation."
                )}
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-border/60 bg-background/80 px-3 py-3 shadow-sm">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/60">
                  {t("vocabulary.card.primaryExampleHeading", "Example")}
                </span>
                <div className="flex items-center gap-1">
                  {stackExampleTranslation && (
                    <button
                      type="button"
                      className="text-[11px] font-medium text-primary transition hover:underline"
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        setShowStackExampleTranslation((prev) => !prev)
                      }}
                    >
                      {showStackExampleTranslation
                        ? t("vocabulary.card.hideTranslation", "Hide translation")
                        : t("vocabulary.card.showTranslation", "Show translation")}
                    </button>
                  )}
                  {stackExampleTimestamp !== null && onTimestampClick && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-full border text-muted-foreground/70 hover:text-primary"
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        onTimestampClick(
                          stackExampleTimestamp,
                          stackExampleEndTimestamp ?? undefined
                        )
                      }}
                      title={`Jump to ${formatTimestamp(stackExampleTimestamp)}`}
                    >
                      <ArrowUpRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              <p
                className="text-sm leading-relaxed text-foreground/90 line-clamp-2 sm:line-clamp-none"
                style={{
                  // fallback for browsers that don't support line-clamp
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                }}
              >
                “{stackExampleText}”
              </p>
              {stackExampleTranslation && showStackExampleTranslation && (
                <p className="mt-2 rounded-2xl bg-muted/40 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground/80">
                  {stackExampleTranslation}
                </p>
              )}
            </div>
          )
        )}

        {!isStackVariant && displayedExamples.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground/60">Examples</p>
            <div className="flex flex-col gap-2">
              {displayedExamples.map((example, index) => {
                // 🔥 key 包含翻译语言，确保翻译更新时重新渲染
                const translationKeys = typeof example?.translation === 'object' && example?.translation 
                  ? Object.keys(example.translation).sort().join(',') 
                  : 'none';
                return (
                  <ExampleCard
                    key={`${term.term || "term"}-${index}-${translationKeys}`}
                    example={example}
                    onTimestampClick={onTimestampClick}
                    userLanguage={userLanguage}
                    isGeneratingTranslation={isGeneratingTranslation}
                  />
                );
              })}
              {remainingExamples > 0 && (
                <span className="rounded-full bg-muted/40 px-3 py-1 text-[11px] text-muted-foreground/80">
                  +{remainingExamples} more
                </span>
              )}
            </div>
          </div>
        )}

        <div className="sr-only">
          {title && <SpeakerIcon divRef={speakRef} lang="en" text={title} />}
        </div>
      </div>

      {(onMarkKnown || onKeepLearning) && (
      <div
        className={cn(
          "mt-auto pt-2 sm:space-y-2 sm:pt-0",
          isStackVariant ? "space-y-2" : "space-y-0"
        )}
      >
        {isStackVariant && hasDetails && (
          <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
            <DrawerTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full font-normal justify-center gap-2 rounded-2xl border border-primary/40 hover:bg-primary/10"
              >
                {t("vocabulary.stack.viewDetails", "View details")}
              </Button>
            </DrawerTrigger>
            <DrawerContent className="max-h-[85vh] rounded-t-3xl border border-border">
              <DrawerHeader className="pb-2 text-left">
                <DrawerTitle className="text-xl font-semibold text-foreground">
                  {title}
                </DrawerTitle>
                {drawerDescriptionLine && (
                  <DrawerDescription>{drawerDescriptionLine}</DrawerDescription>
                )}
              </DrawerHeader>
              <div className="flex-1 overflow-y-auto px-4 pb-6 pt-2 space-y-4">
                {userLanguage && userLanguage !== "zh" && !hasUserLanguageTranslation && !explanation ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-5/6" />
                  </div>
                ) : explanation ? (
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {explanation}
                  </p>
                ) : null}
                {contextDescription && (
                  <div className="rounded-2xl bg-muted/40 p-4 text-sm text-muted-foreground/90">
                    {contextDescription}
                  </div>
                )}
                {examples.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground/60">
                      {t("vocabulary.card.examplesLabel", "Examples")}
                    </p>
                    <div className="space-y-2">
                      {examples.map((example, index) => {
                        // 🔥 key 包含翻译语言，确保翻译更新时重新渲染
                        const translationKeys = typeof example?.translation === 'object' && example?.translation 
                          ? Object.keys(example.translation).sort().join(',') 
                          : 'none';
                        return (
                          <ExampleCard
                            key={`${baseTermKey}-drawer-${index}-${translationKeys}`}
                            example={example}
                            onTimestampClick={onTimestampClick}
                            userLanguage={userLanguage}
                            isGeneratingTranslation={isGeneratingTranslation}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <DrawerFooter className="border-t border-border/80 bg-background/80">
                <DrawerClose asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-center rounded-2xl"
                  >
                    {t("vocabulary.stack.closeDetails", "Close")}
                  </Button>
                </DrawerClose>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        )}
          <div className="pointer-events-none hidden gap-2 opacity-0 transition sm:flex group-hover/term:pointer-events-auto group-hover/term:opacity-100">
            {onMarkKnown && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="flex-1 font-normal justify-center gap-2 rounded-2xl border border-border bg-muted text-foreground hover:bg-white"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  onMarkKnown()
                }}
              >
                {t("vocabulary.desktopActions.markKnown", "Mark as known")}
              </Button>
            )}
            {onKeepLearning && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1 justify-center gap-2 font-normal rounded-2xl border border-primary/30 text-primary hover:bg-primary/10"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  onKeepLearning()
                }}
              >
                {t("vocabulary.desktopActions.keepLearning", "Keep practicing")}
              </Button>
            )}
          </div>
        </div>
      )}

      <Button
        type="button"
        size="icon"
        variant="outline"
        className={cn(
          "absolute sm:bottom-16 bottom-4 right-4 z-20 h-10 w-10 rounded-full border border-border/60 bg-background/90 shadow-sm backdrop-blur transition hover:shadow-md hover:bg-background sm:bottom-3 sm:right-3 sm:h-9 sm:w-9",
          saveButtonDisabled && !isSaved && "opacity-60 cursor-not-allowed",
          isSaved && "border-emerald-300 bg-emerald-50 text-emerald-600"
        )}
        onClick={handleSaveClick}
        disabled={saveButtonDisabled}
        title={saveButtonTitle || saveButtonAccessibleLabel}
        aria-label={saveButtonAccessibleLabel}
      >
        {saveButtonIcon}
      </Button>
    </div>
  )
}

export function VocabularyDisplay({
  data,
  isLoading,
  onTimestampClick,
  forceReveal = false,
  onCardSwipe,
  hiddenTermKeys = [],
  userSettings,
  session,
  episodeId,
  episodeTitle,
  episodeThumbnail,
  isActive = true,
  onVocabularyUpdate,
}: {
  data?: DeepPartial<VocabularyAnalysis>
  isLoading: boolean
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void
  forceReveal?: boolean
  onCardSwipe?: (
    payload: { term: string; scenarioName?: string },
    direction: "left" | "right"
  ) => void
  hiddenTermKeys?: string[]
  userSettings?: any
  session?: any
  episodeId?: string
  episodeTitle?: string
  episodeThumbnail?: string
  isActive?: boolean
  onVocabularyUpdate?: (updatedVocabulary: any) => void
}) {
  const hiddenTermSet = React.useMemo(
    () => new Set(hiddenTermKeys ?? []),
    [hiddenTermKeys]
  )

  const processedScenarios = React.useMemo<ProcessedScenario[]>(() => {
    if (!data?.scenarios) return []

    return data.scenarios.map((scenario: any, index: number) => {
      const rawName = scenario?.name
      const scenarioLabel =
        rawName !== undefined && rawName !== null && rawName !== ""
          ? String(rawName)
          : "Unnamed Scenario"
      // 🔥 修复：在 scenarioValue 中总是包含 index 以确保唯一性
      const scenarioValue =
        rawName !== undefined && rawName !== null && rawName !== ""
          ? `${String(rawName)}-${index}`
          : `scenario-${index}`
      const terms: Partial<VocabularyTerm>[] = Array.isArray(scenario?.terms)
        ? scenario.terms
        : []
      const filteredTerms = terms.filter((termObj) => {
        const key = createVocabularyTermKey(termObj?.term, scenarioLabel)
        if (!key) return true
        return !hiddenTermSet.has(key)
      })

      const termStackItems: TermStackItem[] = filteredTerms.map(
        (termObj, termIndex) => {
          // 🔥 深拷贝 term 对象，确保翻译更新时创建新的引用
          const sanitizedTerm = termObj ? JSON.parse(JSON.stringify(termObj)) : {}
          const backgroundClass = TERM_CARD_BACKGROUND_CLASSES.length
            ? TERM_CARD_BACKGROUND_CLASSES[
                termIndex % TERM_CARD_BACKGROUND_CLASSES.length
              ]
            : "bg-background/95"

          return {
            id: `${scenarioValue}-${termIndex}-${sanitizedTerm.term || "term"}`,
            term: sanitizedTerm,
            scenarioName: scenarioLabel,
            backgroundClass,
          }
        }
      )

      return {
        scenario,
        scenarioValue,
        scenarioLabel,
        termStackItems,
      }
    })
  }, [data, hiddenTermSet])

  const [scenarioStackState, setScenarioStackState] = React.useState<Record<string, ScenarioStackState>>({})

  React.useEffect(() => {
    setScenarioStackState((previous) => {
      let updated = false
      const next: Record<string, ScenarioStackState> = {}

      processedScenarios.forEach(({ scenarioValue, termStackItems }) => {
        const signature = termStackItems.map((item) => item.id).join("|")
        const itemsMap = new Map(termStackItems.map((item) => [item.id, item]))
        const remap = (list: TermStackItem[] = []) =>
          list
            .map((item) => itemsMap.get(item.id))
            .filter((card): card is TermStackItem => Boolean(card))
        const maxVisible = Math.min(STACK_BATCH_SIZE, termStackItems.length)
        const existing = previous[scenarioValue]

        let candidate: ScenarioStackState

        if (!existing || existing.signature !== signature) {
          candidate = {
            visible: termStackItems.slice(0, maxVisible),
            queue: termStackItems.slice(maxVisible),
            dismissed: [],
            signature,
          }
        } else {
          let visible = remap(existing.visible)
          let queue = remap(existing.queue)
          const dismissed = remap(existing.dismissed)

          const used = new Set<string>()
          visible.forEach((card) => used.add(String(card.id)))
          queue.forEach((card) => used.add(String(card.id)))
          dismissed.forEach((card) => used.add(String(card.id)))

          termStackItems.forEach((card) => {
            const key = String(card.id)
            if (!used.has(key)) {
              queue.push(card)
              used.add(key)
            }
          })

          if (visible.length > maxVisible) {
            const overflow = visible.slice(maxVisible)
            visible = visible.slice(0, maxVisible)
            queue = [...overflow, ...queue]
          }

          while (visible.length < maxVisible && queue.length > 0) {
            visible.push(queue.shift()!)
          }

          candidate = {
            visible,
            queue,
            dismissed,
            signature,
          }
        }

        next[scenarioValue] = candidate

        if (
          !existing ||
          existing.signature !== candidate.signature ||
          !arraysEqualById(existing.visible, candidate.visible) ||
          !arraysEqualById(existing.queue, candidate.queue) ||
          !arraysEqualById(existing.dismissed, candidate.dismissed)
        ) {
          updated = true
        }
      })

      const previousKeys = Object.keys(previous)
      const nextKeys = Object.keys(next)

      if (previousKeys.length !== nextKeys.length) {
        updated = true
      } else {
        for (const key of previousKeys) {
          if (!next[key]) {
            updated = true
            break
          }
        }
      }

      return updated ? next : previous
    })
  }, [processedScenarios])

  const { t, lang } = useI18n()
  const { saveCard, savingWord, isCardSaved } = useWordExplanation(
    userSettings,
    undefined,
    session?.user?.id
  )

  const canAttemptSave = React.useMemo(
    () => Boolean(session?.user?.id && userSettings?.myVocabularySetting),
    [session?.user?.id, userSettings?.myVocabularySetting]
  )

  const saveDisabledReason = React.useMemo(() => {
    if (!session?.user?.id) {
      return t("vocabulary.save.loginRequired", "Sign in to save words")
    }
    if (!userSettings?.myVocabularySetting) {
      return t(
        "vocabulary.save.enableSetting",
        "Enable My Vocabulary in settings to save words"
      )
    }
    return ""
  }, [session?.user?.id, userSettings?.myVocabularySetting, t])

  const extractPrimaryExample = React.useCallback((term?: Partial<VocabularyTerm> | null) => {
    if (!term || !Array.isArray(term.examples)) return undefined
    return term.examples.find(
      (example) => Boolean(example?.english) || Boolean(example?.sentence)
    )
  }, [])

  const localizeScenarioLabel = React.useCallback(
    (label: string) => {
      if (!label) return label

      const trimmed = String(label).trim()
      const match = trimmed.match(/^(.*?)\s*\(([^)]+)\)\s*$/)
      if (!match) return trimmed

      const primary = match[1]?.trim() ?? ""
      const secondary = match[2]?.trim() ?? ""

      const containsChinese = (text: string) => /[\u4e00-\u9fff]/.test(text)
      const containsLatin = (text: string) => /[a-zA-Z]/.test(text)

      if (lang === "chinese") {
        if (secondary && containsChinese(secondary)) return secondary
        if (primary && containsChinese(primary)) return primary
        return secondary || primary || trimmed
      }

      if (primary && containsLatin(primary)) return primary
      if (secondary && containsLatin(secondary)) return secondary
      return primary || secondary || trimmed
    },
    [lang]
  )

  const buildContextText = React.useCallback(
    (term?: Partial<VocabularyTerm> | null, scenarioName?: string) => {
      if (!term) return undefined
      const parts: string[] = []
      const context = typeof term.context_description === "string" ? term.context_description.trim() : ""
      if (scenarioName) {
        parts.push(`Scenario: ${scenarioName}`)
      }
      if (context) {
        parts.push(context)
      }
      const primaryExample = extractPrimaryExample(term)
      const exampleText = primaryExample?.english || primaryExample?.sentence
      if (exampleText) {
        parts.push(exampleText)
      }
      if (parts.length === 0) {
        return undefined
      }
      return parts.join(" • ")
    },
    [extractPrimaryExample]
  )

  const createTranslationPayload = React.useCallback((term: Partial<VocabularyTerm>) => {
    const rawExplanation =
      typeof term.explanation === "string" ? term.explanation.trim() : ""
    const translationText = rawExplanation || (term.term ? String(term.term) : "")
    const explains = rawExplanation ? [rawExplanation] : undefined

    return {
      translation: translationText || "",
      explains,
    }
  }, [])

  const handleSaveTerm = React.useCallback(
    async (term?: Partial<VocabularyTerm> | null, scenarioName?: string) => {
      const word = term?.term ? String(term.term).trim() : ""
      if (!word) return

      if (!canAttemptSave) {
        return
      }

      if (isCardSaved(word)) {
        return
      }

      try {
        await saveCard(word, {
          episodeId,
          episodeTitle,
          episodeThumbnail,
          contextText: buildContextText(term, scenarioName),
          status: "NEW",
          youdaoTranslation: createTranslationPayload(term ?? { term: word }),
        })
      } catch (error) {
        console.error("Failed to save vocabulary term:", error)
      }
    },
    [
      buildContextText,
      canAttemptSave,
      createTranslationPayload,
      episodeId,
      episodeThumbnail,
      episodeTitle,
      isCardSaved,
      saveCard,
    ]
  )

  const saveLabel = t("common.save", "Save")
  const savedLabel = t("common.saved", "Saved")
  const savingLabel = t("common.saving", "Saving...")
  const defaultTab = processedScenarios[0]?.scenarioValue || "scenario-0"
  const [activeTab, setActiveTab] = React.useState(defaultTab)
  const [activeStackItem, setActiveStackItem] = React.useState<TermStackItem | null>(null)
  const [isCardStackActive, setIsCardStackActive] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false
    return window.matchMedia("(max-width: 639px)").matches
  })
  const ongoingSpeakAbortRef = React.useRef<AbortController | null>(null)
  const lastSpokenRef = React.useRef<{ id: string; timestamp: number } | null>(null)
  const pendingSpeakTimeoutRef = React.useRef<number | null>(null)
  const ttsModulePromiseRef = React.useRef<Promise<typeof import("@/components/tts")> | null>(null)
  const activeItemIdRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (typeof window === "undefined") return
    ttsModulePromiseRef.current = import("@/components/tts")
  }, [])

  const speakTerm = React.useCallback(async (text: string) => {
    if (!text) return

    try {
      const modulePromise = ttsModulePromiseRef.current ?? import("@/components/tts")
      const { doSpeak } = await modulePromise

      if (ongoingSpeakAbortRef.current) {
        ongoingSpeakAbortRef.current.abort()
      }

      const controller = new AbortController()
      ongoingSpeakAbortRef.current = controller

      try {
        await doSpeak({
          provider: "EdgeTTS",
          text,
          lang: "en",
          rate: 10,
          volume: 100,
          signal: controller.signal,
          onFinish: () => {
            if (ongoingSpeakAbortRef.current === controller) {
              ongoingSpeakAbortRef.current = null
            }
          },
        })
      } finally {
        if (ongoingSpeakAbortRef.current === controller) {
          ongoingSpeakAbortRef.current = null
        }
      }
    } catch (error: any) {
      if (error?.name === "AbortError") return
      console.error("Failed to speak term:", text, error)
    }
  }, [])

  const handleActiveStackChange = React.useCallback(
    (item: TermStackItem | null) => {
      // Only process if we're in card stack mode
      if (!isCardStackActive) {
        return
      }

      // 只有在 isActive 为 true 时才自动朗读
      if (!isActive) {
        activeItemIdRef.current = null
        lastSpokenRef.current = null
        if (pendingSpeakTimeoutRef.current !== null && typeof window !== "undefined") {
          window.clearTimeout(pendingSpeakTimeoutRef.current)
          pendingSpeakTimeoutRef.current = null
        }
        return
      }

      const nextId = item ? String(item.id) : null

      if (nextId === activeItemIdRef.current) {
        return
      }

      activeItemIdRef.current = nextId
      setActiveStackItem(item ?? null)

      if (!item) {
        lastSpokenRef.current = null
        return
      }

      if (!item.term?.term) {
        return
      }

      const itemKey = nextId
      if (!itemKey) return

      const now = Date.now()
      const last = lastSpokenRef.current
      if (last && last.id === itemKey && now - last.timestamp < 1000) {
        return
      }

      if (pendingSpeakTimeoutRef.current !== null && typeof window !== "undefined") {
        window.clearTimeout(pendingSpeakTimeoutRef.current)
        pendingSpeakTimeoutRef.current = null
      }

      const itemTerm = item.term.term

      if (typeof window !== "undefined") {
        lastSpokenRef.current = { id: itemKey, timestamp: now }
        pendingSpeakTimeoutRef.current = window.setTimeout(() => {
          lastSpokenRef.current = { id: itemKey, timestamp: Date.now() }
          void speakTerm(itemTerm ?? "")
        }, 250)
      } else {
        lastSpokenRef.current = { id: itemKey, timestamp: now }
        void speakTerm(itemTerm ?? "")
      }
    },
    [isCardStackActive, speakTerm, isActive]
  )

  const handleCardSwipe = React.useCallback(
    (item: TermStackItem, direction: SwipeDirection) => {
      if (!onCardSwipe) return
      if (direction !== "left" && direction !== "right") return

      const rawTerm = item.term?.term
      if (typeof rawTerm !== "string") return

      const trimmedTerm = rawTerm.trim()
      if (!trimmedTerm) return

      onCardSwipe(
        {
          term: trimmedTerm,
          scenarioName: item.scenarioName,
        },
        direction
      )
    },
    [onCardSwipe]
  )

  const handleStackSwipe = React.useCallback(
    (scenarioValue: string, allItems: TermStackItem[], item: TermStackItem, direction: SwipeDirection) => {
      if (direction !== "left" && direction !== "right") {
        return
      }

      setScenarioStackState((previous) => {
        const current = previous[scenarioValue]
        if (!current) {
          return previous
        }

        const itemsMap = new Map(allItems.map((card) => [card.id, card]))
        const normalize = (list: TermStackItem[]) =>
          dedupeById(
            list
              .map((card) => itemsMap.get(card.id))
              .filter((card): card is TermStackItem => Boolean(card))
          )

        let visible = normalize(current.visible)
        let queue = normalize(current.queue)
        let dismissed = normalize(current.dismissed)

        const normalizedItem = itemsMap.get(item.id) ?? item

        visible = visible.filter((card) => card.id !== item.id)
        queue = queue.filter((card) => card.id !== item.id)
        dismissed = dismissed.filter((card) => card.id !== item.id)

        if (direction === "left") {
          dismissed = dedupeById([...dismissed, normalizedItem])
        } else {
          queue = dedupeById([...queue, normalizedItem])
        }

        const maxVisible = Math.min(STACK_BATCH_SIZE, allItems.length)
        const nextVisible = [...visible]
        const nextQueue = [...queue]

        while (nextVisible.length < maxVisible && nextQueue.length > 0) {
          nextVisible.push(nextQueue.shift()!)
        }

        const candidate: ScenarioStackState = {
          visible: nextVisible,
          queue: nextQueue,
          dismissed,
          signature: current.signature,
        }

        if (
          arraysEqualById(current.visible, candidate.visible) &&
          arraysEqualById(current.queue, candidate.queue) &&
          arraysEqualById(current.dismissed, candidate.dismissed)
        ) {
          return previous
        }

        return {
          ...previous,
          [scenarioValue]: candidate,
        }
      })
    },
    []
  )

  const userLanguage = React.useMemo(() => {
    return userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';
  }, [userSettings]);

  // Check if any explanation or context_note is missing translation for user's language
  const hasMissingTranslations = React.useMemo(() => {
    if (!data?.scenarios || !userLanguage || userLanguage === 'zh') {
      return false; // No need to check if user language is Chinese
    }

    return data.scenarios.some((scenario: any) => {
      if (!scenario?.terms) return false;
      
      return scenario.terms.some((term: any) => {
        // Check explanation translation
        if (term?.explanation) {
          const explanation = term.explanation;
          // If it's a string (legacy format), assume it's Chinese and needs translation
          if (typeof explanation === 'string') {
            return true;
          }
          
          // If it's an object, check if user's language exists and is not empty
          if (typeof explanation === 'object' && explanation !== null) {
            const translation = explanation[userLanguage];
            if (!(translation && typeof translation === 'string' && translation.trim().length > 0)) {
              return true;
            }
          }
        }

        // Check context_note in examples
        if (term?.examples && Array.isArray(term.examples)) {
          const hasMissingContextNote = term.examples.some((example: any) => {
            if (!example?.context_note) return false;
            
            const contextNote = example.context_note;
            // If it's a string (legacy format), assume it's Chinese and needs translation
            if (typeof contextNote === 'string') {
              return true;
            }
            
            // If it's an object, check if user's language exists and is not empty
            if (typeof contextNote === 'object' && contextNote !== null) {
              const translation = contextNote[userLanguage];
              return !(translation && typeof translation === 'string' && translation.trim().length > 0);
            }
            
            return false;
          });
          
          if (hasMissingContextNote) {
            return true;
          }
        }
        
        return false;
      });
    });
  }, [data?.scenarios, userLanguage]);

  // Track if translation generation is in progress
  const [isGeneratingTranslations, setIsGeneratingTranslations] = React.useState(false);
  const translationGenerationAttemptedRef = React.useRef(false);
  
  // 🎯 本地 vocabulary 引用，用于实时更新翻译
  const localVocabularyRef = React.useRef<DeepPartial<VocabularyAnalysis> | null>(null);
  
  // 🎯 翻译进度跟踪
  const translationProgressRef = React.useRef({ completed: 0, total: 0 });
  
  // 同步 data 到 localVocabularyRef
  React.useEffect(() => {
    if (data) {
      localVocabularyRef.current = JSON.parse(JSON.stringify(data)); // Deep clone
    }
  }, [data]);

  // Generate translations for missing explanations
  const generateTranslations = React.useCallback(async () => {
    // 🔍 调试：检查为什么跳过生成
    if (!episodeId) {
      console.log('[VocabularyDisplay] Skipping translations: no episodeId');
      return;
    }
    if (!session?.access_token) {
      console.log('[VocabularyDisplay] Skipping translations: no session or access_token', { 
        hasSession: !!session, 
        hasAccessToken: !!session?.access_token 
      });
      return;
    }
    if (!data) {
      console.log('[VocabularyDisplay] Skipping translations: no data');
      return;
    }
    if (isGeneratingTranslations) {
      console.log('[VocabularyDisplay] Skipping translations: already generating');
      return;
    }
    if (translationGenerationAttemptedRef.current) {
      console.log('[VocabularyDisplay] Skipping translations: already attempted');
      return;
    }

    if (!hasMissingTranslations) {
      return;
    }

    console.log('[VocabularyDisplay] Starting translation generation for', episodeId);

    setIsGeneratingTranslations(true);
    translationGenerationAttemptedRef.current = true;
    
    // 🎯 初始化进度并显示 toast
    translationProgressRef.current = { completed: 0, total: 0 };
    const toastId = 'vocabulary-translation-progress';
    // 只显示一个简单的加载 toast，后续由 start 事件更新
    toast.loading(lang === 'chinese' ? '准备翻译中...' : 'Preparing translation...', { id: toastId });

    try {
      // 🔥 直接从 Supabase 获取最新的 session，确保 token 有效
      const { supabase } = await import('@/core/supabase');
      const { data: { session: freshSession } } = await supabase.auth.getSession();
      
      if (!freshSession?.access_token) {
        console.error('[VocabularyDisplay] No valid session found');
        throw new Error('No valid session found');
      }
      
      const response = await fetch(`/api/episodes/${episodeId}/vocabulary/translations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${freshSession.access_token}`,
        },
        body: JSON.stringify({
          targetLanguage: userSettings?.languageSetting,
          vocabularyFallback: data,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error(`[VocabularyDisplay] Translation API failed:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          episodeId,
        });
        throw new Error(`Translation API failed: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process SSE events - look for complete event blocks
        while (buffer.includes('\n\n')) {
          const boundaryIndex = buffer.indexOf('\n\n');
          const chunk = buffer.slice(0, boundaryIndex);
          buffer = buffer.slice(boundaryIndex + 2);

          if (!chunk.trim()) continue;

          let eventType = '';
          let data = '';

          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              data = line.slice(5).trim();
            }
          }

          if (eventType && data) {
            try {
              const payload = JSON.parse(data);
              
              // 🎯 处理 start 事件，获取翻译总数
              if (eventType === 'start') {
                const totalTerms = payload.totalTerms || 0;
                const totalExamples = payload.totalExamples || 0;
                translationProgressRef.current.total = totalTerms + totalExamples;
                const total = translationProgressRef.current.total;
                // 🚀 并行翻译模式：只显示总数，不显示进度（因为是并行执行）
                toast.loading(
                  lang === 'chinese' ? `正在翻译 ${total} 项...` : `Translating ${total} items...`,
                  { id: toastId }
                );
              } else if (eventType === 'complete' && payload.vocabulary) {
                // Update vocabulary with translations
                if (onVocabularyUpdate) {
                  onVocabularyUpdate(payload.vocabulary);
                }
                // 🎯 显示完成 toast
                const total = translationProgressRef.current.total;
                toast.success(
                  lang === 'chinese' ? `翻译完成！(共 ${total} 项)` : `Translation complete! (${total} items)`,
                  { id: toastId }
                );
              } else if (eventType === 'explanation_translation') {
                // 🎯 实时更新单条解释翻译
                if (onVocabularyUpdate && localVocabularyRef.current) {
                  const { scenarioIndex, termIndex, translation } = payload;
                  const updated = updateSingleExplanation(
                    localVocabularyRef.current,
                    scenarioIndex,
                    termIndex,
                    translation,
                    userLanguage
                  );
                  localVocabularyRef.current = updated;
                  onVocabularyUpdate(updated);
                }
                // 🚀 并行模式下不更新进度数字（会跳跃），只计数
                translationProgressRef.current.completed += 1;
              } else if (eventType === 'example_translation') {
                // 🎯 实时更新单条例句翻译
                if (onVocabularyUpdate && localVocabularyRef.current) {
                  const { scenarioIndex, termIndex, exampleIndex, translation } = payload;
                  const updated = updateSingleExampleTranslation(
                    localVocabularyRef.current,
                    scenarioIndex,
                    termIndex,
                    exampleIndex,
                    translation,
                    userLanguage
                  );
                  localVocabularyRef.current = updated;
                  onVocabularyUpdate(updated);
                }
                // 🚀 并行模式下不更新进度数字（会跳跃），只计数
                translationProgressRef.current.completed += 1;
              } else if (eventType === 'context_note_translation') {
                // 🎯 实时更新单条 context_note 翻译
                if (onVocabularyUpdate && localVocabularyRef.current) {
                  const { scenarioIndex, termIndex, exampleIndex, translation } = payload;
                  const updated = updateSingleContextNote(
                    localVocabularyRef.current,
                    scenarioIndex,
                    termIndex,
                    exampleIndex,
                    translation,
                    userLanguage
                  );
                  localVocabularyRef.current = updated;
                  onVocabularyUpdate(updated);
                  
                  // 🎯 更新进度 toast（context_note 不算在总数里，只是额外的翻译）
                }
              } else if (eventType === 'error') {
                console.error('Translation generation error:', payload.message);
                translationGenerationAttemptedRef.current = false; // Allow retry on error
                // 🎯 显示错误 toast
                const errorMsg = payload.message || 'Unknown error';
                toast.error(
                  lang === 'chinese' ? `翻译失败: ${errorMsg}` : `Translation failed: ${errorMsg}`,
                  { id: toastId }
                );
              }
            } catch (e) {
              console.error('Failed to parse SSE payload', e);
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Failed to generate translations:', error);
      // 🔥 401 错误（权限问题）不应该重试，其他错误可以重试
      const is401Error = error?.message?.includes('401');
      if (!is401Error) {
        translationGenerationAttemptedRef.current = false; // Allow retry on non-auth errors
      }
      // 🎯 显示错误 toast
      toast.error(
        lang === 'chinese' ? '翻译失败，请重试' : 'Translation failed. Please try again.',
        { id: toastId }
      );
    } finally {
      setIsGeneratingTranslations(false);
    }
  }, [
    episodeId,
    session?.access_token,
    data,
    hasMissingTranslations,
    isGeneratingTranslations,
    userSettings?.languageSetting,
    onVocabularyUpdate,
  ]);

  // Auto-generate translations when missing translations are detected
  React.useEffect(() => {
    if (!isLoading && hasMissingTranslations && !isGeneratingTranslations && !translationGenerationAttemptedRef.current) {
      // Small delay to avoid calling immediately on mount
      const timer = setTimeout(() => {
        generateTranslations();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, hasMissingTranslations, isGeneratingTranslations, generateTranslations]);

  // Reset translation generation state when data updates (after translation is complete)
  React.useEffect(() => {
    // If translations are no longer missing, reset the generation state
    if (!hasMissingTranslations && translationGenerationAttemptedRef.current) {
      translationGenerationAttemptedRef.current = false;
      setIsGeneratingTranslations(false);
    }
  }, [hasMissingTranslations]);

  // Check if a specific term is missing translation (explanation or examples)
  const isTermMissingTranslation = React.useCallback((term: Partial<VocabularyTerm> | null | undefined): boolean => {
    if (!userLanguage || userLanguage === 'zh') {
      return false;
    }

    // Check explanation
    if (term?.explanation) {
      const explanation = term.explanation;
      if (typeof explanation === 'string') {
        return true; // Legacy string format needs translation
      }
      if (typeof explanation === 'object' && explanation !== null && !explanation[userLanguage]) {
        return true;
      }
    }
    
    // 🔥 Also check examples translations
    if (term?.examples && Array.isArray(term.examples)) {
      for (const example of term.examples) {
        const translation = example?.translation;
        if (!translation) {
          return true; // No translation at all
        }
        if (typeof translation === 'string') {
          return true; // Legacy string format needs translation
        }
        if (typeof translation === 'object' && translation !== null && !translation[userLanguage]) {
          return true; // Missing user's language translation
        }
      }
    }
    
    return false;
  }, [userLanguage]);

  const buildTermCardProps = React.useCallback(
    (item: TermStackItem) => {
      const rawTerm = item.term?.term
      const word = typeof rawTerm === "string" ? rawTerm.trim() : ""
      const lowerWord = word.toLowerCase()
      const isSaving =
        Boolean(word && savingWord[word]) ||
        (lowerWord ? Boolean(savingWord[lowerWord]) : false)
      const isSaved = Boolean(word && isCardSaved(word))
      const canSaveWord = canAttemptSave && Boolean(word)
      const disabledReasonForWord =
        isSaved
          ? savedLabel
          : !canSaveWord && saveDisabledReason
            ? saveDisabledReason
            : undefined

      const onSaveHandler =
        canSaveWord && !isSaved
          ? () => handleSaveTerm(item.term, item.scenarioName)
          : undefined

      const createSwipeHandler = (direction: "left" | "right") => {
        if (!onCardSwipe) return undefined
        const raw = item.term?.term
        if (typeof raw !== "string") return undefined
        const trimmed = raw.trim()
        if (!trimmed) return undefined
        return () =>
          onCardSwipe(
            {
              term: trimmed,
              scenarioName: item.scenarioName,
            },
            direction
          )
      }

      // Check if this specific term is missing translation
      const termMissingTranslation = isTermMissingTranslation(item.term)

      return {
        term: item.term,
        scenarioName: item.scenarioName,
        onTimestampClick,
        forceReveal,
        backgroundClass: item.backgroundClass,
        onSave: onSaveHandler,
        isSaving,
        isSaved,
        saveLabel,
        savedLabel,
        savingLabel,
        disabledReason: disabledReasonForWord,
        onMarkKnown: createSwipeHandler("left"),
        onKeepLearning: createSwipeHandler("right"),
        userLanguage,
        isGeneratingTranslation: isGeneratingTranslations && termMissingTranslation,
      }
    },
    [
      canAttemptSave,
      forceReveal,
      handleSaveTerm,
      isCardSaved,
      onTimestampClick,
      onCardSwipe,
      saveDisabledReason,
      saveLabel,
      savedLabel,
      savingLabel,
      savingWord,
      userLanguage,
      isGeneratingTranslations,
      isTermMissingTranslation,
    ]
  )

  React.useEffect(() => {
    if (typeof window === "undefined") return

    const mediaQuery = window.matchMedia("(max-width: 639px)")

    const handleChange = (event: MediaQueryListEvent) => {
      setIsCardStackActive(event.matches)
    }

    setIsCardStackActive(mediaQuery.matches)

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange)
      return () => mediaQuery.removeEventListener("change", handleChange)
    }

    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  React.useEffect(() => {
    if (!isCardStackActive || !isActive) {
      if (activeStackItem !== null) {
        setActiveStackItem(null)
      }
      activeItemIdRef.current = null
      lastSpokenRef.current = null
      if (pendingSpeakTimeoutRef.current !== null && typeof window !== "undefined") {
        window.clearTimeout(pendingSpeakTimeoutRef.current)
        pendingSpeakTimeoutRef.current = null
      }
      if (ongoingSpeakAbortRef.current) {
        ongoingSpeakAbortRef.current.abort()
      }
    }
  }, [isCardStackActive, activeStackItem, isActive])

  React.useEffect(() => {
    if (processedScenarios.length === 0) {
      setActiveTab(defaultTab)
      setActiveStackItem(null)
      lastSpokenRef.current = null
      return
    }

    const scenarioValues = processedScenarios.map((entry) => entry.scenarioValue)

    setActiveTab((previous) => {
      if (scenarioValues.includes(previous)) {
        return previous
      }
      return scenarioValues[0] ?? defaultTab
    })
  }, [processedScenarios, defaultTab])

  React.useEffect(() => {
    const matchedScenario = processedScenarios.find(
      (entry) => entry.scenarioValue === activeTab
    )

    if (!matchedScenario || matchedScenario.termStackItems.length === 0) {
      if (activeStackItem !== null) {
        setActiveStackItem(null)
      }
      activeItemIdRef.current = null
      lastSpokenRef.current = null
      if (pendingSpeakTimeoutRef.current !== null && typeof window !== "undefined") {
        window.clearTimeout(pendingSpeakTimeoutRef.current)
        pendingSpeakTimeoutRef.current = null
      }
    }
  }, [activeTab, processedScenarios, activeStackItem])

  React.useEffect(() => () => {
    if (pendingSpeakTimeoutRef.current !== null && typeof window !== "undefined") {
      window.clearTimeout(pendingSpeakTimeoutRef.current)
      pendingSpeakTimeoutRef.current = null
    }
    if (ongoingSpeakAbortRef.current) {
      ongoingSpeakAbortRef.current.abort()
    }
  }, [])

  return (
    <div className="pt-0 sm:pt-3 flex flex-col w-full max-w-full overflow-hidden">
      <style jsx global>{`
        .scrollbar-hide {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      {isLoading ? (
        <div className="space-y-4 px-4">
          <div className="space-y-3">
            <Skeleton className="h-10 sm:h-12 w-full rounded-lg" />
            <Skeleton className="h-24 sm:h-32 w-full rounded-lg" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-10 sm:h-12 w-full rounded-lg" />
            <Skeleton className="h-24 sm:h-32 w-full rounded-lg" />
          </div>
        </div>
      ) : processedScenarios.length > 0 ? (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="relative mb-1 sm:mb-4 px-4 sm:px-0 sm:hidden">
            <div
              className="scrollbar-hide overflow-x-auto overflow-y-hidden"
              style={{
                width: "100vw",
                marginLeft: "calc(-1 * (100vw - 100%) / 2 - 12px)",
                marginRight: "calc(-1 * (100vw - 100%) / 2 - 12px)",
                paddingLeft: "calc((100vw - 100%) / 2)",
                paddingRight: "calc((100vw - 100%) / 2)",
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <TabsList
                className="inline-flex flex-nowrap px-3 gap-2 rounded-2xl bg-transparent sm:bg-muted/50 p-1"
                style={{ minWidth: "max-content", justifyContent: "flex-start" }}
              >
                {processedScenarios.map(({ scenarioLabel, scenarioValue }) => {
                  const displayLabel = localizeScenarioLabel(scenarioLabel)
                  return (
                    <TabsTrigger
                      key={scenarioValue}
                      value={scenarioValue}
                      className="flex-shrink-0 whitespace-nowrap rounded-xl px-3 py-2 text-xs transition-all duration-200 data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-medium data-[state=active]:border"
                      title={displayLabel}
                    >
                      {displayLabel}
                    </TabsTrigger>
                  )
                })}
              </TabsList>
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-background to-transparent hidden sm:block" />
            <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-background to-transparent hidden sm:block" />
          </div>

          <div className="mb-4 hidden sm:block">
            <div className="overflow-x-auto scrollbar-hide px-4">
              <TabsList className="inline-flex min-w-full w-max rounded-3xl bg-none p-1">
                {processedScenarios.map(({ scenarioLabel, scenarioValue }) => {
                  const displayLabel = localizeScenarioLabel(scenarioLabel)
                  return (
                    <TabsTrigger
                      key={scenarioValue}
                      value={scenarioValue}
                      className="flex-shrink-0 font-light whitespace-nowrap rounded-3xl px-4 py-2 text-md transition-all duration-200 data-[state=active]:shadow-sm data-[state=active]:border-foreground/40 data-[state=active]:border data-[state=active]:font-normal"
                    >
                      {displayLabel}
                    </TabsTrigger>
                  )
                })}
              </TabsList>
            </div>
          </div>

          {processedScenarios.map(({ scenarioLabel, scenarioValue, termStackItems }) => {
            const stackState = scenarioStackState[scenarioValue]
            // 🔥 确保从最新的 termStackItems 获取数据，而不是使用缓存的 stackState.visible
            const termItemsMap = new Map(termStackItems.map((item) => [item.id, item]))
            const visibleStackItems = (() => {
              if (stackState && stackState.visible.length > 0) {
                // 从 stackState 获取 id 顺序，但从最新的 termStackItems 获取实际数据
                return stackState.visible
                  .map((item) => termItemsMap.get(item.id))
                  .filter((item): item is TermStackItem => Boolean(item))
              }
              return termStackItems.slice(0, Math.min(STACK_BATCH_SIZE, termStackItems.length))
            })()

            return (
              <TabsContent
                key={scenarioValue}
                value={scenarioValue}
                className="m-0 w-full border-0 p-0"
              >
                <div className="w-full space-y-2 sm:space-y-4 px-8 pb-4 sm:px-4">
                  {scenarioValue === activeTab &&
                    isCardStackActive &&
                    termStackItems.length > 0 && (
                      <div className="sm:hidden">
                        {/* 小屏幕下隐藏 */}
                        <div className="hidden sm:rounded-2xl rounded-xl border border-dashed border-primary/30 bg-primary/5 sm:px-4 px-2 sm:py-3 py-2 sm:block">
                          {/* <div className="flex items-center gap-2 text-xs font-medium text-primary">
                            <Badge
                              variant="secondary"
                              className="bg-primary/10 text-primary"
                            >
                              {t("vocabulary.swipeTip.badge", "Tip")}
                            </Badge>
                            <span>
                              {t(
                                "vocabulary.swipeTip.heading",
                                "Swipe to manage this stack"
                              )}
                            </span>
                          </div> */}
                          <div className="flex items-start gap-2 text-xs text-muted-foreground/80">
                            <ArrowLeftRight className="mt-0.5 h-4 w-4 text-muted-foreground/60" />
                            <p>
                              {t(
                                "vocabulary.swipeTip.description",
                                "Swipe left to hide words you know, or right to keep them in your practice flow."
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  {termStackItems.length > 0 ? (
                    <div className="sm:hidden px-4">
                      <div className="flex justify-center">
                        <CardStack<TermStackItem>
                          items={visibleStackItems}
                          containerClassName="relative mx-auto sm:h-[24rem] h-[18rem] w-full max-w-[380px]"
                          cardClassName="absolute inset-0 h-full w-full cursor-grab"
                          stackConfig={{ rotation: 2, scale: 0.04, perspective: 900 }}
                          onActiveChange={handleActiveStackChange}
                          onItemSwipe={(item, direction) => {
                            if (scenarioValue !== activeTab || !isCardStackActive) return
                            handleStackSwipe(scenarioValue, termStackItems, item, direction)
                            handleCardSwipe(item, direction)
                          }}
                        >
                          {(item) => {
                            const termCardProps = buildTermCardProps(item)
                            return <TermCard {...termCardProps} variant="stack" />
                          }}
                        </CardStack>
                      </div>
                    </div>
                  ) : (
                    <div className="sm:hidden px-4 text-center text-sm text-muted-foreground">
                      No vocabulary terms available for this scenario.
                    </div>
                  )}

                  <div className="hidden gap-4 sm:grid sm:grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
                    {termStackItems.map((item) => {
                      const termCardProps = buildTermCardProps(item)
                      return <TermCard key={item.id} {...termCardProps} variant="grid" />
                    })}
                  </div>
                </div>
              </TabsContent>
            )
          })}
        </Tabs>
      ) : (
        <div className="py-12 px-4 text-center">
          <p className="text-muted-foreground">No vocabulary data available.</p>
        </div>
      )}
    </div>
  )
}
